import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSiteWallCameras } from "./useSiteWallCameras";
import { SiteVideoWallMiniPanel } from "./SiteVideoWallMiniPanel";
import {
  SiteVideoWallFullDialog,
  SiteVideoWallFullMode,
} from "./SiteVideoWallFullDialog";
import {
  getFocusGridPageForCamera,
  buildSiteVideoWallPath,
  SiteVideoWallAlertContext,
} from "./siteVideoWallUtils";
import { WALL_GRID_PROTOCOL } from "./siteVideoWallConstants";
import { WallStreamsProvider } from "./WallStreams";

export type { SiteVideoWallAlertContext };

interface SiteVideoWallProps {
  siteId: number | null;
  siteName: string;
  alertContext: SiteVideoWallAlertContext;
  defaultFocusedCameraId: string | null;
  miniOpen: boolean;
  onMiniOpenChange: (open: boolean) => void;
}

export const SiteVideoWall: React.FC<SiteVideoWallProps> = ({
  siteId,
  siteName,
  alertContext,
  defaultFocusedCameraId,
  miniOpen,
  onMiniOpenChange,
}) => {
  const [fullOpen, setFullOpen] = useState(false);
  const [miniPage, setMiniPage] = useState(0);
  const [fullPage, setFullPage] = useState(0);
  const [fullMode, setFullMode] = useState<SiteVideoWallFullMode>("focus");
  const [focusedCameraId, setFocusedCameraId] = useState<string | null>(
    defaultFocusedCameraId
  );
  const focusGridPageSyncedRef = useRef(false);

  const camerasEnabled = miniOpen || fullOpen;
  const { status, cameras, totalCount, ensureCamerasLoaded } =
    useSiteWallCameras(siteId, camerasEnabled);

  useEffect(() => {
    setFocusedCameraId(defaultFocusedCameraId);
  }, [siteId]);

  useEffect(() => {
    if (fullOpen) {
      setFocusedCameraId(defaultFocusedCameraId);
    }
  }, [fullOpen, defaultFocusedCameraId]);

  useEffect(() => {
    setMiniPage(0);
    setFullPage(0);
    setFullOpen(false);
    onMiniOpenChange(false);
    focusGridPageSyncedRef.current = false;
  }, [siteId, onMiniOpenChange]);

  useEffect(() => {
    if (!fullOpen) {
      focusGridPageSyncedRef.current = false;
    }
  }, [fullOpen]);

  useEffect(() => {
    if (!fullOpen || fullMode !== "focus" || status !== "ready" || !focusedCameraId) {
      return;
    }
    if (focusGridPageSyncedRef.current) return;

    const pageForCamera = getFocusGridPageForCamera(focusedCameraId, cameras);
    if (pageForCamera !== null) {
      setFullPage(pageForCamera);
      focusGridPageSyncedRef.current = true;
      return;
    }

    if (cameras.length < totalCount) {
      void ensureCamerasLoaded(totalCount - 1);
    }
  }, [
    fullOpen,
    fullMode,
    status,
    focusedCameraId,
    cameras,
    totalCount,
    ensureCamerasLoaded,
  ]);

  const handleCloseMini = useCallback(() => {
    onMiniOpenChange(false);
  }, [onMiniOpenChange]);

  const handleExpand = useCallback(() => {
    onMiniOpenChange(false);
    setFullOpen(true);
  }, [onMiniOpenChange]);

  const handleCloseFull = useCallback(() => {
    setFullOpen(false);
  }, []);

  const handleModeChange = useCallback((mode: SiteVideoWallFullMode) => {
    setFullMode(mode);
    if (mode === "focus") {
      focusGridPageSyncedRef.current = false;
    } else {
      setFullPage(0);
    }
  }, []);

  const alertContextLabel = `${alertContext.detectionType} · ${alertContext.cameraName} · ${alertContext.formattedDate}`;

  const expandHref =
    siteId != null
      ? buildSiteVideoWallPath({
          siteId,
          siteName,
          cameraId: defaultFocusedCameraId,
          alertContext,
        })
      : undefined;

  return (
    // One stream registry across both panels: a camera's player persists across
    // mini ⇆ full and Focus ⇆ Equal ⇆ page changes (its <video> is re-parented
    // into the new tile slot) instead of being torn down + re-requested.
    <WallStreamsProvider
      protocol={WALL_GRID_PROTOCOL}
      lowLatency={false}
      enabled={camerasEnabled}
    >
      <SiteVideoWallMiniPanel
        open={miniOpen}
        onClose={handleCloseMini}
        onExpand={handleExpand}
        expandHref={expandHref}
        status={status}
        cameras={cameras}
        totalCount={totalCount}
        page={miniPage}
        onPageChange={setMiniPage}
        ensureCamerasLoaded={ensureCamerasLoaded}
      />
      <SiteVideoWallFullDialog
        open={fullOpen}
        onClose={handleCloseFull}
        siteName={siteName}
        alertContextLabel={alertContextLabel}
        status={status}
        cameras={cameras}
        totalCount={totalCount}
        mode={fullMode}
        onModeChange={handleModeChange}
        page={fullPage}
        onPageChange={setFullPage}
        focusedCameraId={focusedCameraId}
        onFocusedCameraChange={setFocusedCameraId}
        ensureCamerasLoaded={ensureCamerasLoaded}
      />
    </WallStreamsProvider>
  );
};
